import {
  ActionsUnion,
  IActiveTripResponseV2,
  IRiderPortalUser,
  IPortalProduct,
  ISavedLocations,
  IService,
  IZone,
  IPortalTripFilters,
  IIntegrationProductResponse,
  IAvailabilityDTO
} from 'src/types'
import { createAction } from '../helpers'
import * as constants from '../../constants'
import { riderService } from 'src/services/Rider'
import { riderPortalService } from 'src/services/rider-portal'
import { tripService } from 'src/services/Trip'
import { IAvailableProductsPayload, ICreateTripBooking } from 'src/types/rider-portal'
import { TripStatuses } from 'src/constants/trip-statuses'

export const actionCreators = {
  resetCurrentlyViewedTripsData: () => createAction(constants.RESET_CURRENTLY_VIEWED_TRIPS),
  setAvailableProducts: (products: IPortalProduct[]) => createAction(constants.SET_AVAILABLE_PRODUCTS, products),
  setAvailableServices: (services: IService[]) =>
    createAction(constants.SET_AVAILABLE_SERVICE_BY_CURRENT_LOCATION, services),
  setCurrentlyViewedTrips: (trip: IActiveTripResponseV2) => createAction(constants.SET_CURRENTLY_VIEWED_TRIPS, trip),
  setLoadingMetadata: (type: string, status: boolean) => createAction(constants.SET_LOADING_METADATA, { type, status }),
  setLoggedInRiderDetails: (riderDetails: IRiderPortalUser) =>
    createAction(constants.SET_LOGGED_IN_RIDER_DETAILS, riderDetails),
  setRiderSavedLocations: (locations: ISavedLocations[]) =>
    createAction(constants.SET_RIDER_SAVED_LOCATIONS, locations),
  setServiceDesignatedZone: (designatedZones: Record<string, IZone[]>) =>
    createAction(constants.SET_SERVICE_DESIGNATED_ZONES, designatedZones),
  setServiceZones: (zones: Record<string, IZone[]>) => createAction(constants.SET_SERVICE_ZONES, zones)
}

export type IRiderPortalActions = ActionsUnion<typeof actionCreators>

export const cancelTrip = (tripId: string, comment: string) => async (dispatch: any) => {
  const { setLoadingMetadata } = actionCreators
  dispatch(setLoadingMetadata('updatingTrip', true))
  return tripService
    .updateStatus(tripId, comment, TripStatuses.CANCELLED)
    .then(data => {
      if (data && typeof data === 'string') {
        return { success: true, message: 'Trip successfully cancelled' }
      }
      return { success: false, message: 'Failed to cancel the trip' }
    })
    .catch(error => {
      console.log('Failed to cancel the trip: ', error)
      return { success: false, message: 'Failed to cancel the trip, check the logs for more details' }
    })
    .finally(() => dispatch(setLoadingMetadata('updatingTrip', false)))
}

export const createTripBooking = (payload: ICreateTripBooking) => (dispatch: any) => {
  const { setLoadingMetadata } = actionCreators
  dispatch(setLoadingMetadata('requestingTripBooking', true))
  return riderPortalService
    .createTripBooking(payload)
    .then((data: { id: string }) => {
      if (data.id) {
        return data
      }
      return {}
    })
    .catch(error => {
      console.log('Failed to book a trip: ', error)
      return {}
    })
    .finally(() => dispatch(setLoadingMetadata('requestingTripBooking', false)))
}

export const getAvailableProducts = (payload: IAvailableProductsPayload, riderId: string) => (dispatch: any) => {
  const { setAvailableProducts, setLoadingMetadata } = actionCreators
  dispatch(setLoadingMetadata('fetchingProducts', true))
  return riderPortalService
    .getAvailableProducts(payload, riderId)
    .then(async (data: IIntegrationProductResponse[]) => {
      if (data.length > 0) {
        const serviceProducts = data
          .filter((product: { serviceId: string }) => product.serviceId === payload.serviceId)
          .map((product: IIntegrationProductResponse) => {
            if (product.id) {
              const { description, id, name } = product
              const { destination, origin, timezone } = payload
              const dto: { product: IIntegrationProductResponse; tripRequest: IAvailabilityDTO } = {
                product: { ...product, productId: id, productName: name },
                tripRequest: { destination, origin, timezone }
              }

              return tripService
                .fetchIntegrationProduct(riderId, dto)
                .then(({ data }) => {
                  return { ...data, description }
                })
                .catch(error => {
                  console.log('Failed to fetch product integration: ', error)
                  return null
                })
            }
            return null
          })
        const availableProducts = await Promise.all(serviceProducts)
        dispatch(setAvailableProducts(availableProducts))
      }
    })
    .catch(error => console.log('Failed to fetch available products: ', error))
    .finally(() => dispatch(setLoadingMetadata('fetchingProducts', false)))
}

export const getRiderSavedLocations = (riderId: string) => (dispatch: any) => {
  const { setRiderSavedLocations, setLoadingMetadata } = actionCreators
  dispatch(setLoadingMetadata('fetchingSavedLocations', true))
  return riderService
    .getRiderLocationList(riderId)
    .then(data => {
      if (data.locations) {
        const locationsArr = Object.values(data.locations).filter(
          (location: ISavedLocations) => !location.isDeleted
        ) as ISavedLocations[]
        dispatch(setRiderSavedLocations(locationsArr))
      }
    })
    .catch(error => console.log('Failed to fetch rider saved locations: ', error))
    .finally(() => dispatch(setLoadingMetadata('fetchingSavedLocations', false)))
}

export const getServicesByCurrentLocation = (
  currentLocation: google.maps.LatLngLiteral,
  orgId: string,
  type: string
) => (dispatch: any) => {
  const { setAvailableServices, setLoadingMetadata } = actionCreators
  dispatch(setLoadingMetadata('fetchingServices', true))
  return riderPortalService
    .getServicesByCurrenLocation(currentLocation, orgId, type)
    .then(data => {
      if (data.length > 0) {
        dispatch(setAvailableServices(data))
      }
    })
    .catch(error => console.log('Failed to fetch available services: ', error))
    .finally(() => dispatch(setLoadingMetadata('fetchingServices', false)))
}

export const getServiceDesignatedZones = (serviceId: string) => (dispatch: any) => {
  const { setServiceDesignatedZone } = actionCreators
  return riderPortalService
    .getServiceDesignatedZones(serviceId)
    .then((data: IZone[]) => {
      if (data.length > 0) {
        dispatch(setServiceDesignatedZone({ [serviceId]: data }))
      }
    })
    .catch(error => console.log(`Failed to fetch designated service zones for ${serviceId}: `, error))
}

export const getServiceZones = (organizationId: string, serviceId: string) => (dispatch: any) => {
  const { setServiceZones } = actionCreators
  return riderPortalService
    .getServiceZones(organizationId, serviceId)
    .then((data: IZone[]) => {
      if (data.length > 0) {
        dispatch(setServiceZones({ [serviceId]: data }))
      }
    })
    .catch(error => console.log(`Failed to fetch service zones for ${serviceId}: `, error))
}

export const getTripDetails = (tripId: string, initialFetch: boolean) => (dispatch: any) => {
  const { setCurrentlyViewedTrips, setLoadingMetadata } = actionCreators
  if (initialFetch) {
    dispatch(setLoadingMetadata('fetchingTripDetails', true))
  }
  return tripService
    .get(tripId)
    .then((data: IActiveTripResponseV2) => {
      if (data) {
        dispatch(setCurrentlyViewedTrips(data))
        return data
      }
      return {}
    })
    .catch(error => {
      console.log('Failed to fetch trip details: ', error)
      return {}
    })
    .finally(() => dispatch(setLoadingMetadata('fetchingTripDetails', false)))
}



// WEBPACK FOOTER //
// ./src/state/actions/rider-portal/main.ts